/**
 * Amorçage de l'analytique produit, avant l'hydratation.
 *
 * Convention Next.js : ce fichier s'exécute côté client avant que
 * l'application ne devienne interactive, et l'export `onRouterTransitionStart`
 * intercepte les navigations client.
 *
 * Ces vues de page comptent particulièrement ici : `StudioPage` est monté en
 * permanence et les routes `/studio*` rendent `null`, donc **aucune** mesure
 * fondée sur le montage de composants ne verrait la navigation. C'est le seul
 * endroit qui l'observe de façon fiable.
 */
import { startAnalytics, track, flush, type NavigationType } from "@/lib/analytics";
import { describeUncaughtError } from "@/lib/analytics/uncaught-error";
import { observePageVisibility, renderTrail } from "@/lib/analytics/trail";

/**
 * Exécute une étape de mesure sans jamais laisser une exception remonter.
 *
 * Ce module est évalué **avant l'hydratation** : une exception au niveau du
 * module fait échouer l'évaluation de tout le bundle client, React ne s'attache
 * jamais, et l'interface rendue par le serveur reste affichée mais
 * définitivement inerte. Rien ne le signale à l'écran, ce qui en fait une panne
 * particulièrement coûteuse à diagnostiquer.
 *
 * Le cas fondateur : `crypto.randomUUID`, absent des contextes non sécurisés,
 * donc de la build Capacitor qui charge l'interface en clair depuis l'IP du
 * Mac. La cause est corrigée à la source, mais la classe de pannes demeure,
 * une API manquante suffisant à la rejouer. L'invariant de `lib/analytics`
 * — la mesure ne peut pas casser l'application — se tient donc aussi ici.
 *
 * On trace en console plutôt que d'avaler en silence : visible en
 * développement, sans conséquence en production.
 */
function safely(step: string, run: () => void): void {
  try {
    run();
  } catch (error) {
    console.warn(`[analytics] ${step} : mesure abandonnée`, error);
  }
}

/**
 * La page en cours, et depuis quand.
 *
 * Tenu ici parce que c'est le seul endroit qui voie les deux bouts : l'arrivée
 * et le départ. `window.location` ne suffit pas au moment du départ, puisque
 * `onRouterTransitionStart` est appelé avant que l'URL ne change et que
 * `pagehide` arrive quand il n'y a plus rien à lire.
 */
let currentPathname = "";
let enteredAt = 0;

/**
 * Ouvre la page courante. À appeler juste après avoir émis sa vue.
 */
function enterPage(pathname: string): void {
  currentPathname = pathname;
  enteredAt = Date.now();
}

/**
 * Ferme la page courante.
 *
 * # Pourquoi cet événement existe
 *
 * `page_viewed` part chez PostHog sous son nom natif `$pageview`, ce qui
 * allume les rapports intégrés. Mais ces rapports raisonnent par **paires** :
 * le SDK web émet `$pageview` et `$pageleave`, et plusieurs chiffres se
 * calculent sur le second. Le taux de rebond en particulier, dont la
 * définition officielle est « une seule vue de page, aucune autocapture, et
 * moins de dix secondes sur la page » : sans signal de sortie, la durée n'est
 * jamais établie et **toute** visite d'une seule page compte comme un rebond,
 * qu'on y soit resté trois secondes ou trois minutes. Idem pour la durée de
 * session, qui s'arrête au dernier événement reçu.
 *
 * Nous n'avons pas d'autocapture : la collecte est déclarative et relayée par
 * le serveur. Cet événement est donc le seul signal de fin de page qui
 * existe.
 *
 * # Ce qu'il ne voit pas
 *
 * Un onglet tué en arrière-plan par l'OS n'émet pas `pagehide`, et sa dernière
 * page n'a donc pas de sortie. C'est la limite du SDK officiel aussi. On ne
 * sort **pas** sur `visibilitychange` pour autant : un retour d'arrière-plan
 * produirait une seconde sortie pour une seule vue, ce qui casserait la paire
 * que ce couple d'événements existe précisément pour former.
 */
function leavePage(): void {
  if (currentPathname === "" || enteredAt === 0) {
    return;
  }

  const pathname = currentPathname;
  const durationMs = Date.now() - enteredAt;

  currentPathname = "";
  enteredAt = 0;

  track({ name: "page_left", properties: { pathname, durationMs } });
}

safely("démarrage", () => {
  startAnalytics();

  // Le fil de contexte des rapports de panne commence ici, avec la page :
  // l'état du contexte audio s'y ajoute quand le studio le crée.
  observePageVisibility(document);

  track({
    name: "page_viewed",
    properties: { pathname: window.location.pathname, navigationType: "load" },
  });

  enterPage(window.location.pathname);
});

window.addEventListener("pagehide", () => {
  safely("sortie de page", () => {
    leavePage();

    // Le client vide sa file à `pagehide` et s'y est abonné au démarrage, donc
    // **avant** ce gestionnaire : sans ce vidage explicite, la sortie serait
    // mise en file après le sien, et perdue au moment précis où elle est le
    // seul événement qui reste à envoyer.
    flush();
  });
});

/**
 * Range la chaîne que passe Next.js dans le vocabulaire de la taxonomie.
 *
 * Le routeur ne promet rien de plus qu'une `string` : un `as NavigationType`
 * ferait entrer une valeur inconnue dans PostHog sous un nom que personne
 * n'attend, où elle deviendrait une modalité de plus dans chaque ventilation
 * — exactement ce que le vocabulaire fermé existe pour empêcher. Le `switch`
 * fait la vérification à l'exécution et TypeScript en tire le rétrécissement
 * tout seul, sans conversion.
 *
 * Le repli est `push` et non l'absence de valeur : on est ici sur une
 * transition du routeur client, donc jamais sur un chargement, et une
 * navigation sans type sortirait des ventilations au lieu d'y entrer sous la
 * modalité la plus probable.
 */
function navigationTypeOf(value: string): NavigationType {
  switch (value) {
    case "load":
    case "push":
    case "replace":
    case "traverse":
      return value;
    default:
      return "push";
  }
}

export function onRouterTransitionStart(href: string, navigateType: string): void {
  safely("navigation", () => {
    let pathname = href;

    try {
      pathname = new URL(href, window.location.href).pathname;
    } catch {
      // `href` inattendu : on remonte la valeur brute plutôt que rien.
    }

    /*
      Une navigation qui ne change pas de chemin n'est pas un changement de page.

      Le catalogue écrit sa recherche et ses filtres dans la query string, et
      chaque affinage stabilisé déclenche une transition du routeur : sans ce
      garde, une seule recherche coûtait une sortie **et** une vue de plus, la
      durée de la page repartait de zéro à chaque frappe, et les rapports de
      chemins comptaient dix vues de `/library` pour une seule consultation.

      Le chemin, et pas l'URL entière : c'est bien la même page, et c'est la
      page que la mesure décrit. Ce que la query string change relève de
      `catalog_searched`, qui existe pour ça.
    */
    if (pathname === currentPathname) {
      return;
    }

    // La sortie avant l'arrivée : l'ordre est celui du récit, et il permet à
    // la sortie de porter le chemin qu'on quitte, encore intact ici.
    leavePage();

    track({
      name: "page_viewed",
      properties: { pathname, navigationType: navigationTypeOf(navigateType) },
    });

    enterPage(pathname);
  });
}

/**
 * Erreurs non rattrapées : la santé technique d'une bêta se joue surtout sur
 * ce que l'utilisateur ne signale jamais. On tronque la pile, seule sa tête
 * porte l'information utile au tri.
 *
 * Le garde-fou compte double sur ce gestionnaire : une exception levée ici
 * émettrait à son tour un événement `error`, qui le rappellerait, en boucle.
 */
window.addEventListener("error", (event) => {
  safely("report d'erreur", () => {
    const report = describeUncaughtError(event);

    track({
      name: "app_error",
      properties: {
        message: report.message,
        stackHead: report.stackHead,
        route: window.location.pathname,
        fatal: true,
        opaque: report.opaque,
        trail: renderTrail() ?? undefined,
      },
    });

    flush();
  });
});

window.addEventListener("unhandledrejection", (event) => {
  safely("report de rejet", () => {
    const reason = event.reason;

    track({
      name: "app_error",
      properties: {
        message: (reason instanceof Error ? reason.message : String(reason)).slice(0, 300),
        stackHead: reason instanceof Error ? (reason.stack ?? "").slice(0, 500) : undefined,
        route: window.location.pathname,
        fatal: false,
        trail: renderTrail() ?? undefined,
      },
    });
  });
});
